import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, TagIcon, PlusIcon } from '@heroicons/react/24/outline';

/**
 * TagSelector - Multi-select tag picker with search and custom tag creation
 * 
 * @param {Object} props
 * @param {Object} props.supabase - Supabase client instance (required)
 * @param {Array} props.selectedTags - Currently selected tags
 * @param {Function} props.onTagsChange - Callback when tags change
 * @param {string} props.placeholder - Search input placeholder
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.theme - 'dark' or 'light' theme
 */
const TagSelector = ({ 
  supabase,
  selectedTags = [], 
  onTagsChange, 
  placeholder = "Search for services like plumbing, web design, fishing charter...",
  className = "",
  theme = "dark",
  // #1695: lets a form label the search box and mark the picker required for assistive tech.
  // aria-required rather than required: the box is only a search field, and native required
  // would block submit whenever it is empty even with tags selected.
  inputId,
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [popularTags, setPopularTags] = useState([]);
  const [searchResolution, setSearchResolution] = useState(null); // Track what was searched and how it was resolved
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Theme-aware styles
  const styles = theme === 'dark' ? {
    input: 'bg-slate-700/50 border-slate-600/50 placeholder-gray-500 text-white focus:ring-blue-500 focus:border-blue-500',
    dropdown: 'bg-slate-800 border-slate-700 border',
    dropdownItem: 'text-white hover:bg-slate-700',
    helperText: 'text-gray-400'
  } : {
    input: 'bg-white border-gray-300 placeholder-gray-400 text-gray-900 focus:ring-blue-500 focus:border-blue-500',
    dropdown: 'bg-white border-gray-300 border shadow-xl',
    dropdownItem: 'text-gray-900 hover:bg-gray-50',
    helperText: 'text-gray-600'
  };

  useEffect(() => {
    if (supabase) loadPopularTags();
  }, [supabase]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      searchTags(searchTerm);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const loadPopularTags = async () => {
    try {
      /**
       * Board #1700. This selected EVERY row of `tags` with no filter, so a customer
       * posting a marketplace request was offered all 193 company-created custom tags
       * next to the 20 curated ones, plus the 8 `facet` and 5 `domain` tags which are not
       * services at all. Live 2026-09-21 that meant 'iris-test-tag', 'orion-audit-tag',
       * 'iris-carl-keyboard-test', 'orion-test-2' and the single characters 'a', 'o', 'or'
       * appeared as choosable service categories.
       *
       * It was a cross-tenant disclosure too, not just untidy: `tags_read_all` is SELECT
       * to role `public` with qual `true`, so one company's internal tag names were
       * readable by anonymous visitors here.
       *
       * Curated + global IS the customer-facing taxonomy (Jerry's ruling 2026-09-21:
       * internal service tags and customer-facing categories are distinct).
       */
      const { data, error } = await supabase
        .from('tags')
        .select('id, name, category')
        .eq('tag_kind', 'service')
        .eq('is_curated', true)
        .is('company_id', null)
        .order('name', { ascending: true })
        .limit(20);

      if (error) throw error;
      setPopularTags(data || []);
    } catch (error) {
      console.error('Error loading popular tags:', error);
    }
  };

  const searchTags = async (term) => {
    if (!supabase) return;
    try {
      setIsSearching(true);
      setSearchResolution(null);

      // Board #1700: same narrowing as loadPopularTags above — searching must not reach
      // tags the picker is not allowed to offer.
      const { data, error } = await supabase
        .from('tags')
        .select('id, name, category')
        .eq('tag_kind', 'service')
        .eq('is_curated', true)
        .is('company_id', null)
        .ilike('name', `%${term.trim()}%`)
        .order('name', { ascending: true })
        .limit(20);

      if (error) {
        console.error('Error searching tags:', error);
        setSearchResults([]);
        return;
      }

      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching tags:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const addTag = (tag) => {
    if (!selectedTags.find(t => t.id === tag.id)) {
      const newTags = [...selectedTags, tag];
      onTagsChange(newTags);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  const removeTag = (tagId) => {
    const newTags = selectedTags.filter(t => t.id !== tagId);
    onTagsChange(newTags);
  };

  // ✅ UNIFIED: Custom tag creation disabled - use global tags only
  // If new tags are needed, they must be added by admins to the tags table
  const createCustomTag = async (tagName) => {
    // No-op: Custom tags not allowed in unified system
    console.log('Custom tags disabled. Please select from available service tags.');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      const exactMatch = searchResults.find(tag =>
        tag.name.toLowerCase() === searchTerm.toLowerCase().trim()
      );
      if (exactMatch) {
        addTag(exactMatch);
      } else {
        createCustomTag(searchTerm);
      }
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'TRADES': 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
      'HOME_SERVICES': 'bg-green-500/20 text-green-300 border border-green-500/30',
      'PROFESSIONAL_SERVICES': 'bg-purple-500/20 text-purple-300 border border-purple-500/30',
      'CREATIVE': 'bg-pink-500/20 text-pink-300 border border-pink-500/30',
      'SPECIALIZED': 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
      'TECHNOLOGY': 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30',
      'CUSTOM': 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
    };
    return colors[category] || colors.CUSTOM;
  };

  const displayTags = searchTerm.trim().length >= 2 ? searchResults : popularTags;
  const filteredTags = displayTags.filter(tag =>
    !selectedTags.find(selected => selected.id === tag.id)
  );

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <span
              key={tag.id}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(tag.category)}`}
            >
              <TagIcon className="h-3 w-3 mr-1" />
              {tag.name}
              <button
                type="button"
                onClick={() => removeTag(tag.id)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={searchInputRef}
            id={inputId}
            aria-required={required || undefined}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-md sm:text-sm ${styles.input}`}
            placeholder={placeholder}
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>

        {/* Dropdown - Constrained with max-height and proper scrolling */}
        {showDropdown && (
          <div className={`absolute top-full left-0 right-0 z-50 mt-1 rounded-lg max-h-48 py-1 overflow-y-auto ${styles.dropdown}`}>
            {/* Search Resolution Label - Shows what was searched and how it was resolved */}
            {searchResolution && filteredTags.length > 0 && (
              <div className={`px-4 py-2 text-xs font-medium border-b ${theme === 'dark' ? 'border-slate-700 text-blue-400' : 'border-gray-200 text-blue-600'}`}>
                Results for "{searchResolution.userSearched}" • {searchResolution.resolvedTo}
              </div>
            )}

            {filteredTags.length === 0 && searchTerm.trim().length >= 2 && !isSearching && (
              <div className={`px-4 py-2 text-sm ${styles.helperText} flex items-center`}>
                <PlusIcon className="h-4 w-4 mr-2 text-blue-400" />
                No tags found for "{searchTerm}"
              </div>
            )}

            {filteredTags.length === 0 && searchTerm.trim().length < 2 && (
              <div className={`px-4 py-2 text-sm ${styles.helperText}`}>
                Type at least 2 characters to search...
              </div>
            )}

            {filteredTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => addTag(tag)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${styles.dropdownItem}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TagIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="font-medium">{tag.name}</span>
                  </div>
                  {tag.category && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(tag.category)}`}>
                      {tag.category.replace('_', ' ').toLowerCase()}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className={`text-xs ${styles.helperText}`}>
        Search for services like "plumbing", "web design", "fishing charter", etc.
        <span className="block mt-1">
          💡 Tags are used for categorization and discovery only. You can select multiple tags.
        </span>
        {/* Board #1700: this promised something that does not happen. createCustomTag()
            here is a deliberate no-op ("Custom tags disabled" — customers do not author
            the taxonomy), so pressing Enter created nothing and said nothing, which reads
            as a broken control. Say what is actually true instead. */}
        {searchTerm.trim().length >= 2 && filteredTags.length === 0 && (
          <span className="block mt-1 text-blue-400">
            No service matches “{searchTerm.trim()}”. Try a broader word — pick the closest
            category and describe the specifics in your request.
          </span>
        )}
      </p>
    </div>
  );
};

export default TagSelector;

